import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
  Button,
  Flex,
  Group,
  Notification,
  Paper,
  Select,
  Stack,
  Switch,
  TextInput,
  Title,
  Card,
  Grid,
  Text,
  Badge,
  Loader,
  InputWrapper,
  ActionIcon,
  Alert,
  Table,
  NumberInput,
  Textarea,
  NumberFormatter,
  Tabs,
  Highlight,
  ScrollArea,
  FileInput,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useParams } from 'react-router';
import {
  IconArrowLeft,
  IconAt,
  IconBuildingHospital,
  IconPhone,
  IconUser,
  IconTrash,
  IconAlertCircle,
  IconEdit,
  IconSearch,
  IconPhoto,
} from '@tabler/icons-react';
import { HealthcareFacilitiesApi } from '../../../global/apis/HealthcareFacilitiesApi/HealthcareFacilitiesApi.ts';
import { useDeleteHealthcareFacilityLogo } from '../../../global/entities/healthcareFacility/mutations/useDeleteHealthcareFacilityLogo.tsx';
import { modals } from '@mantine/modals';
import { usePostHealthcareFacility } from '../../../global/entities/healthcareFacility/mutations/usePostHealthcareFacilityMutations.tsx';
import { useGetHealthcareFacilityById } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilityById.tsx';
import { usePutHealthcareFacility } from '../../../global/entities/healthcareFacility/mutations/usePutHealthcareFacilityMutations.tsx';
import { useNavigate } from 'react-router-dom';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { useGetHealthcareFacilityNew } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilityNew.tsx';
import { IHealthcareFacilityForm } from '../../../global/entities/healthcareFacility/HealthcareFacility.ts';
import { theme } from '../../../global/style/theme.tsx';
import { useGetClinicSeatUsage } from '../../../global/entities/clinicSubscription/queries/useGetClinicSeatUsage.tsx';
import { useCreateClinicSubscription } from '../../../global/entities/clinicSubscription/mutations/useCreateClinicSubscription.tsx';
import { useUpdateClinicSubscription } from '../../../global/entities/clinicSubscription/mutations/useUpdateClinicSubscription.tsx';
import { useReleaseClinicSeat } from '../../../global/entities/clinicSubscription/mutations/useReleaseClinicSeat.tsx';
import { useDeleteClinicSubscription } from '../../../global/entities/clinicSubscription/mutations/useDeleteClinicSubscription.tsx';
import {
  ICreateClinicSubscription,
  IUpdateClinicSubscription,
} from '../../../global/entities/clinicSubscription/ClinicSubscription.ts';
import TherapeuticIndicationMultiSelect from '../components/TherapeuticIndicationMultiSelect';
import QuestionnaireMultiSelect from '../components/QuestionnaireMultiSelect';
import FeatureMultiSelect from '../components/FeatureMultiSelect';
import DefaultTasksConfig from '../components/DefaultTasksConfig';
import { useGetTherapeuticIndications } from '../../../global/entities/TherapeuticIndication/queries/useGetTherapeuticIndications';
import { useGetQuestionnaires } from '../../../global/entities/Questionnaires/queries/useGetQuestionnaires';
import { useAllFeatures } from '../../../global/entities/Feature/queries';
import HcfEICFTemplatesPanel from './HcfEICFTemplatesPanel.tsx';
import HcfInvitationMessagePanel from './HcfInvitationMessagePanel.tsx';
import HcfRegistrationFieldsPanel from './HcfRegistrationFieldsPanel.tsx';
import HcfQuestionnaireExportButton from './HcfQuestionnaireExportButton.tsx';
import { useTranslation } from 'react-i18next';
import { useGetDefaultTasksByFacilityId } from '../../../global/entities/healthcareFacility/queries/useGetDefaultTasksByFacilityId';
import { usePostDefaultTasksForFacility } from '../../../global/entities/healthcareFacility/mutations/usePostDefaultTasksForFacility';
import { IHealthcareFacilityDefaultTaskInput } from '../../../global/entities/healthcareFacility/HealthcareFacility';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';

const initValues = {
  id: null,
  healthcareFacilityTypeID: 0,
  name: '',
  address: '',
  address2: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  contactName: '',
  contactRole: '',
  contactEmail: '',
  contactPhone: '',
  isAnonymizedMode: false,
  therapeuticIndicationIds: [] as number[],
  questionnaireIds: [] as number[],
  featureIds: [] as number[],
};

const HcfForm: React.FC = inject()(
  observer(() => {
    const MAX_CLINIC_SEATS = 10000;

    const params = useParams<{ id: string; tabId: string }>();
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState<string | null>(
      null,
    );
    const [showCreateSubscriptionForm, setShowCreateSubscriptionForm] =
      useState(false);
    const [isEditingSubscription, setIsEditingSubscription] = useState(false);
    const postHealthcareFacility = usePostHealthcareFacility();
    const putHealthcareFacility = usePutHealthcareFacility();
    const { data: hcfData } = useGetHealthcareFacilityById();
    const { data: hcfNew } = useGetHealthcareFacilityNew();
    const navigate = useNavigate();
    const {
      data: subscription,
      isLoading: isLoadingSubscription,
      error: subscriptionLoadError,
    } = useGetClinicSeatUsage(params.id && params.id !== '0' ? params.id : '');
    const { data: therapeuticIndications } = useGetTherapeuticIndications();
    const { data: questionnaires } = useGetQuestionnaires();
    const { data: allFeatures } = useAllFeatures();
    const createClinicSubscriptionMutation = useCreateClinicSubscription();
    const updateClinicSubscriptionMutation = useUpdateClinicSubscription();
    const releaseClinicSeatMutation = useReleaseClinicSeat();
    const deleteClinicSubscriptionMutation = useDeleteClinicSubscription();
    const { t } = useTranslation();
    const facilityId =
      params.id && params.id !== '0' ? parseInt(params.id) : undefined;
    const { data: defaultTasks } = useGetDefaultTasksByFacilityId(
      facilityId,
      facilityId !== undefined,
    );
    const postDefaultTasksMutation = usePostDefaultTasksForFacility();
    const [defaultTasksState, setDefaultTasksState] = useState<
      IHealthcareFacilityDefaultTaskInput[]
    >([]);
    const {
      setSorting,
      sortBy,
      reverseSortDirection,
      setSortedData,
      sortedData,
    } = useTable();
    const [searchUsers, setSearchUsers] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoError, setLogoError] = useState<string | null>(null);
    const MAX_LOGO_SIZE = 2 * 1024 * 1024;
    const deleteLogoMutation = useDeleteHealthcareFacilityLogo();

    // Validation schema
    const schema = z.object({
      healthcareFacilityTypeID: z
        .number()
        .min(1, { message: t('formError.healthFacilityTypeIsRequired') }),
      name: z
        .string()
        .min(1, { message: t('formError.healthFacilityNameIsRequired') }),
      address: z
        .string()
        .min(1, { message: t('formError.addressLine1IsRequired') }),
      address2: z.string().optional(),
      city: z.string().min(1, { message: t('formError.cityIsRequired') }),
      province: z
        .string()
        .min(1, { message: t('formError.provinceIsRequired') }),
      country: z.string().min(1, { message: t('formError.countryIsRequired') }),
      postalCode: z
        .string()
        .min(1, { message: t('formError.postalCodeIsRequired') }),
      contactRole: z
        .string()
        .min(1, { message: t('formError.contactNameIsRequired') }),
      contactName: z
        .string()
        .min(1, { message: t('formError.contactNameIsRequired') }),
      contactEmail: z
        .string()
        .email({ message: t('formError.invalidEmailAddress') }),
      contactPhone: z
        .string()
        .min(1, { message: t('formError.phoneIsRequired') }),
    });

    const form = useForm<IHealthcareFacilityForm>({
      mode: 'controlled',
      initialValues: initValues as unknown as IHealthcareFacilityForm,
      validate: zodResolver(schema),
    });

    const handleCancel = () => {
      form.reset();
      navigate('/adminDashboard/hcf');
    };

    const clearLogoLocally = () => {
      setLogoFile(null);
      setLogoError(null);
      form.setFieldValue('logoFilename', '');
    };

    const handleRemoveLogo = () => {
      // A freshly picked (not yet uploaded) file, or an unsaved facility:
      // nothing on the server to delete, just clear the local selection.
      if (logoFile || !facilityId || !form.values.logoFilename) {
        clearLogoLocally();
        return;
      }

      modals.openConfirmModal({
        title: t('formHcf.removeLogoTitle'),
        children: <Text>{t('formHcf.removeLogoConfirm')}</Text>,
        labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
        confirmProps: { color: 'red' },
        cancelProps: { variant: 'outline', color: 'gray' },
        onConfirm: async () => {
          await deleteLogoMutation.mutateAsync(facilityId);
          clearLogoLocally();
        },
      });
    };

    const handleSubmit = async (formValues: IHealthcareFacilityForm) => {
      setError(null);
      setIsSubmitting(true);
      try {
        // Filter out invalid IDs (0, negative, null, undefined)
        const cleanedValues = {
          ...formValues,
          therapeuticIndicationIds: (
            formValues.therapeuticIndicationIds || []
          ).filter((id) => id > 0),
          questionnaireIds: (formValues.questionnaireIds || []).filter(
            (id) => id > 0,
          ),
          featureIds: (formValues.featureIds || []).filter((id) => id > 0),
        };

        const result = await (
          params.id !== '0' ? putHealthcareFacility : postHealthcareFacility
        ).mutateAsync(cleanedValues);
        const savedFacilityId =
          params.id && params.id !== '0' ? parseInt(params.id) : result.id;

        // Upload the logo image to S3 if a new one was selected
        if (logoFile && savedFacilityId) {
          const policy = await HealthcareFacilitiesApi.getLogoUploadUrl(
            savedFacilityId,
            encodeURIComponent(logoFile.name),
          );
          const logoFormData = new FormData();
          logoFormData.append('key', policy.key);
          logoFormData.append('policy', policy.policy);
          logoFormData.append('x-amz-algorithm', policy['x-amz-algorithm']);
          logoFormData.append('x-amz-credential', policy['x-amz-credential']);
          logoFormData.append('x-amz-date', policy['x-amz-date']);
          logoFormData.append('x-amz-signature', policy['x-amz-signature']);
          logoFormData.append('file', logoFile);

          const s3Response = await fetch(policy.url, {
            method: 'POST',
            body: logoFormData,
          });
          if (!s3Response.ok)
            throw new Error(`S3 upload failed (${s3Response.status})`);
        }

        // Save default tasks if facility ID is available
        if (savedFacilityId && defaultTasksState.length >= 0) {
          // Filter out invalid default tasks
          const validDefaultTasks = defaultTasksState.filter(
            (task) => task.questionnaireID > 0,
          );
          await postDefaultTasksMutation.mutateAsync({
            facilityId: savedFacilityId,
            tasks: validDefaultTasks,
          });
        }

        console.log(t('submissionSuccess'));
      } catch (e) {
        if (e instanceof AppMedError) setError(e.message);
        else setError('An unexpected error occurred.');
      } finally {
        setIsSubmitting(false);
        handleCancel();
      }
    };

    const subscriptionForm = useForm<ICreateClinicSubscription>({
      initialValues: {
        clinicID: params.id && params.id !== '0' ? parseInt(params.id) : 0,
        totalSeats: 10,
        durationMonths: 12,
        pricePerSeat: 50.0,
        notes: '',
      },
      validate: {
        clinicID: (value) =>
          value <= 0
            ? t('clinicSubscriptions.form.validation.selectClinic')
            : null,
        totalSeats: (value) =>
          value <= 0
            ? t('clinicSubscriptions.form.validation.totalSeatsGreaterThanZero')
            : null,
        durationMonths: (value) =>
          value <= 0
            ? t('clinicSubscriptions.form.validation.durationGreaterThanZero')
            : null,
        pricePerSeat: (value) =>
          value <= 0
            ? t('clinicSubscriptions.form.validation.priceGreaterThanZero')
            : null,
      },
    });

    const editSubscriptionForm = useForm<IUpdateClinicSubscription>({
      initialValues: {
        totalSeats: subscription?.totalSeats,
        durationMonths: subscription?.durationMonths,
        pricePerSeat: subscription?.pricePerSeat,
        notes: subscription?.notes || '',
      },
      validate: {
        totalSeats: (value) =>
          value !== undefined && value <= 0
            ? t('clinicSubscriptions.form.validation.totalSeatsGreaterThanZero')
            : null,
        durationMonths: (value) =>
          value !== undefined && value <= 0
            ? t('clinicSubscriptions.form.validation.durationGreaterThanZero')
            : null,
        pricePerSeat: (value) =>
          value !== undefined && value <= 0
            ? t('clinicSubscriptions.form.validation.priceGreaterThanZero')
            : null,
      },
    });

    useEffect(() => {
      if (hcfData && form.values.id !== hcfData.id) {
        form.reset();
        form.setValues({
          ...hcfData,
          therapeuticIndicationIds: (
            hcfData.therapeuticIndicationIds || []
          ).filter((id) => id > 0),
          questionnaireIds: (hcfData.questionnaireIds || []).filter(
            (id) => id > 0,
          ),
          featureIds: (hcfData.featureIds || []).filter((id) => id > 0),
        });
      }
    }, [hcfData, hcfNew, params.id, form]);

    useEffect(() => {
      if (params.id && params.id !== '0')
        subscriptionForm.setFieldValue('clinicID', parseInt(params.id));
    }, [params.id]);

    useEffect(() => {
      if (subscription && !isEditingSubscription) {
        editSubscriptionForm.setValues({
          totalSeats: subscription.totalSeats,
          durationMonths: subscription.durationMonths,
          pricePerSeat: subscription.pricePerSeat,
          notes: subscription.notes || '',
        });
      }
    }, [subscription, isEditingSubscription]);

    useEffect(() => {
      if (defaultTasks) {
        setDefaultTasksState(
          defaultTasks.map((task) => ({
            questionnaireID: task.questionnaireID,
            availableDateOffsetDays: task.availableDateOffsetDays,
            dueDateOffsetDays: task.dueDateOffsetDays,
            isRecurring: task.isRecurring,
            recurrenceType: task.recurrenceType,
            recurrenceInterval: task.recurrenceInterval,
            recurrenceEndOffsetDays: task.recurrenceEndOffsetDays,
            expirationDays: task.expirationDays,
          })),
        );
      }
    }, [defaultTasks]);

    useEffect(() => {
      if (subscription?.activeUsers) setSortedData(subscription.activeUsers);
      else setSortedData([]);
    }, [subscription?.activeUsers, setSortedData]);

    const handleCreateSubscription = async (
      values: ICreateClinicSubscription,
    ) => {
      setSubscriptionError(null);
      try {
        await createClinicSubscriptionMutation.mutateAsync(values);
        setShowCreateSubscriptionForm(false);
        subscriptionForm.reset();
      } catch (err: any) {
        setSubscriptionError(
          err.response?.data?.error ||
            t('clinicSubscriptions.errors.createError'),
        );
      }
    };

    const handleUpdateSubscription = async (
      values: IUpdateClinicSubscription,
    ) => {
      if (!subscription?.subscriptionID) return;

      setSubscriptionError(null);
      try {
        await updateClinicSubscriptionMutation.mutateAsync({
          subscriptionId: subscription.subscriptionID.toString(),
          data: values,
        });
        setIsEditingSubscription(false);
      } catch (err: any) {
        setSubscriptionError(
          err.response?.data?.error ||
            t('clinicSubscriptions.errors.updateError') ||
            'Failed to update subscription',
        );
      }
    };

    const handleReleaseSeat = async (userId: string) => {
      if (!params.id || params.id === '0') return;

      try {
        await releaseClinicSeatMutation.mutateAsync({
          clinicId: params.id,
          userId,
        });
      } catch {
        setSubscriptionError(t('clinicSubscriptions.errors.releaseSeatError'));
      }
    };

    const openReleaseSeatModal = (userId: string) => {
      modals.openConfirmModal({
        title: t('modal.releaseClinicSeatModal.title'),
        children: (
          <Stack gap="sm">
            <Text>{t('modal.releaseClinicSeatModal.question')}</Text>
            <Text fw="bold">{t('modal.releaseClinicSeatModal.details')}</Text>
          </Stack>
        ),
        labels: {
          confirm: t('button.delete'),
          cancel: t('button.cancel'),
        },
        confirmProps: { color: 'red' },
        cancelProps: { variant: 'outline', color: 'gray' },
        onConfirm: () => handleReleaseSeat(userId),
        size: 'lg',
        radius: 'xs',
      });
    };

    const handleDeleteSubscription = async () => {
      if (!subscription?.subscriptionID) return;

      try {
        await deleteClinicSubscriptionMutation.mutateAsync(
          subscription.subscriptionID.toString(),
        );
      } catch {
        setSubscriptionError(t('clinicSubscriptions.errors.deleteError'));
      }
    };

    const openDeleteSubscriptionModal = () => {
      modals.openConfirmModal({
        title: t('modal.deleteClinicSubscriptionModal.title'),
        children: (
          <Stack gap="sm">
            <Text>{t('modal.deleteClinicSubscriptionModal.question')}</Text>
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                {t('modal.deleteClinicSubscriptionModal.warning')}
              </Text>
            </Alert>
            <Text fw="bold">
              {t('modal.deleteClinicSubscriptionModal.details')}
            </Text>
          </Stack>
        ),
        labels: {
          confirm: t('button.delete'),
          cancel: t('button.cancel'),
        },
        confirmProps: { color: 'red' },
        cancelProps: { variant: 'outline', color: 'gray' },
        onConfirm: handleDeleteSubscription,
        size: 'lg',
        radius: 'xs',
      });
    };

    return (
      <>
        <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
          <Button
            leftSection={
              <IconArrowLeft color={theme.colors?.blue?.toString()} />
            }
            onClick={() => navigate('/adminDashboard/hcf')}
            variant="transparent"
          >
            <Title c="black" order={4}>
              {`${hcfData?.name || t('hcf.addNewHCF')}`}
            </Title>
          </Button>
        </Flex>
        <Tabs defaultValue="clinic" mt="md">
          <Tabs.List>
            <Tabs.Tab value="clinic">
              {t('formHcf.clinicInformation') || 'Clinic Information'}
            </Tabs.Tab>
            {params.id !== '0' && (
              <Tabs.Tab value="eicfTemplates">
                {t('eicf.admin.facilityTabTitle')}
              </Tabs.Tab>
            )}
            {params.id !== '0' && (
              <Tabs.Tab value="invitationEmail">
                {t('invitationMessage.admin.tabTitle')}
              </Tabs.Tab>
            )}
            {params.id !== '0' && (
              <Tabs.Tab value="registrationFields">
                {t('registrationFields.admin.tabTitle')}
              </Tabs.Tab>
            )}
            {params.id !== '0' && (
              <Tabs.Tab value="subscription">
                {t('clinicSubscriptions.form.subscriptionOverview') ||
                  'Subscription'}
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="clinic" pt="md">
            <Paper>
              <form
                onSubmit={form.onSubmit((values) =>
                  handleSubmit(values as unknown as IHealthcareFacilityForm),
                )}
              >
                <Stack>
                  <TextInput
                    label={t('formHcf.healthFacilityName')}
                    placeholder={t('formGlobal.enterHealthFacilityName')}
                    {...form.getInputProps('name')}
                  />
                  <Select
                    label={t('formHcf.healthFacilityType')}
                    placeholder={t('formHcf.selectHealthFacilityType')}
                    leftSection={<IconBuildingHospital size={16} />}
                    data={hcfNew?.healthcareFacilitiesCombobox}
                    onChange={(value) =>
                      form.setFieldValue(
                        'healthcareFacilityTypeID',
                        Number(value),
                      )
                    }
                    value={
                      form.values.healthcareFacilityTypeID > 0
                        ? form.values.healthcareFacilityTypeID.toString()
                        : null
                    }
                    error={form.getInputProps('healthcareFacilityTypeID').error}
                  />
                  <TextInput
                    label={t('formGlobal.addressLine1')}
                    placeholder={t('formGlobal.enterCivicNumberAndStreetName')}
                    {...form.getInputProps('address')}
                  />
                  <TextInput
                    label={t('formGlobal.addressLine2Optional')}
                    placeholder={t('formGlobal.enterAdditionalAddressInfo')}
                    {...form.getInputProps('address2')}
                  />
                  <Group grow>
                    <TextInput
                      label={t('formGlobal.city')}
                      placeholder={t('formGlobal.enterCity')}
                      {...form.getInputProps('city')}
                    />
                    <TextInput
                      label={t('formGlobal.provinceOrState')}
                      placeholder={t('formGlobal.enterProvinceOrState')}
                      {...form.getInputProps('province')}
                    />
                  </Group>
                  <Group grow>
                    <TextInput
                      label={t('formGlobal.country')}
                      placeholder={t('formGlobal.enterCountry')}
                      {...form.getInputProps('country')}
                    />
                    <TextInput
                      label={t('formGlobal.postalCode')}
                      maxLength={7}
                      placeholder={t('formGlobal.enterPostalCode')}
                      {...form.getInputProps('postalCode')}
                    />
                  </Group>
                  <Group grow align="end">
                    <TextInput
                      label={t('formGlobal.contactRole')}
                      maxLength={100}
                      placeholder={t('formGlobal.enterContactRole')}
                      leftSection={<IconUser size={16} />}
                      {...form.getInputProps('contactRole')}
                    />
                    <TextInput
                      label={t('formGlobal.contactName')}
                      maxLength={100}
                      placeholder={t('formGlobal.enterContactPersonName')}
                      leftSection={<IconUser size={16} />}
                      {...form.getInputProps('contactName')}
                    />
                  </Group>
                  <Group grow align="end">
                    <TextInput
                      label={t('formGlobal.contactEmailAddress')}
                      placeholder={t('formGlobal.enterContactEmail')}
                      leftSection={<IconAt size={16} />}
                      {...form.getInputProps('contactEmail')}
                    />
                    <TextInput
                      label={t('formGlobal.contactPhoneNumber')}
                      maxLength={50}
                      placeholder={t('formGlobal.enterContactPhoneNumber')}
                      leftSection={<IconPhone size={16} />}
                      {...form.getInputProps('contactPhone')}
                    />
                  </Group>
                  <Switch
                    label={t('formInvitation.anonymizedParticipantMode')}
                    description={t(
                      'formInvitation.anonymizedParticipantModeDescription',
                    )}
                    {...form.getInputProps('isAnonymizedMode', {
                      type: 'checkbox',
                    })}
                  />
                  <Stack gap={4}>
                    <FileInput
                      label={t('formHcf.clinicLogo')}
                      description={t('formHcf.clinicLogoDescription')}
                      placeholder={
                        form.values.logoFilename || t('formHcf.selectLogoFile')
                      }
                      accept="image/png,image/jpeg,image/webp"
                      leftSection={<IconPhoto size={16} />}
                      clearable
                      value={logoFile}
                      error={logoError}
                      onChange={(file) => {
                        setLogoError(null);
                        if (file && file.size > MAX_LOGO_SIZE) {
                          setLogoError(t('formHcf.logoTooLarge'));
                          return;
                        }
                        setLogoFile(file);
                        form.setFieldValue('logoFilename', file?.name ?? '');
                      }}
                    />
                    {form.values.logoFilename && (
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-sm"
                        leftSection={<IconTrash size={14} />}
                        loading={deleteLogoMutation.isPending}
                        onClick={handleRemoveLogo}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        {t('formHcf.removeLogo')}
                      </Button>
                    )}
                  </Stack>
                  <InputWrapper
                    size={'sm'}
                    styles={{ label: { fontSize: 16, fontWeight: 'bold' } }}
                    label={t('formHcf.availableTherapeuticIndications')}
                  >
                    <TherapeuticIndicationMultiSelect
                      value={form.values.therapeuticIndicationIds}
                      onChange={(values) => {
                        console.log(
                          'Therapeutic indication selection changed:',
                          values,
                        );
                        form.setFieldValue('therapeuticIndicationIds', values);
                      }}
                      isError={false}
                      therapeuticIndicationsData={therapeuticIndications || []}
                      placeholder={t('formHcf.selectTherapeuticIndications')}
                    />
                  </InputWrapper>
                  <InputWrapper
                    size={'sm'}
                    styles={{ label: { fontSize: 16, fontWeight: 'bold' } }}
                    label={t('formHcf.availableQuestionnaires')}
                  >
                    <QuestionnaireMultiSelect
                      value={form.values.questionnaireIds}
                      onChange={(values) =>
                        form.setFieldValue('questionnaireIds', values)
                      }
                      isError={false}
                      questionnairesData={questionnaires || []}
                      placeholder={t('formHcf.selectQuestionnaires')}
                    />
                  </InputWrapper>
                  <InputWrapper
                    size={'sm'}
                    styles={{ label: { fontSize: 16, fontWeight: 'bold' } }}
                    label={t('formHcf.availableFeatures')}
                    description={t(
                      'featureManagement.medicalInformationAlwaysVisibleHcp',
                    )}
                  >
                    <FeatureMultiSelect
                      value={form.values.featureIds}
                      onChange={(values) =>
                        form.setFieldValue('featureIds', values)
                      }
                      isError={false}
                      featuresData={allFeatures || []}
                      placeholder={t('formHcf.selectFeatures')}
                    />
                  </InputWrapper>
                  {params.id !== '0' && (
                    <DefaultTasksConfig
                      value={defaultTasksState}
                      onChange={setDefaultTasksState}
                      questionnairesData={questionnaires || []}
                      accessibleQuestionnaireIds={form.values.questionnaireIds}
                    />
                  )}
                  {facilityId !== undefined && (
                    <HcfQuestionnaireExportButton facilityId={facilityId} />
                  )}
                  <Group>
                    <Button
                      ml={'auto'}
                      variant="default"
                      onClick={handleCancel}
                    >
                      {t('button.cancel')}
                    </Button>
                    <Button type="submit" loading={isSubmitting}>
                      {t(params.id ? 'button.save' : 'button.save')}
                    </Button>
                  </Group>
                </Stack>
              </form>
              {error && (
                <Notification color="red" onClose={() => setError(null)}>
                  {error}
                </Notification>
              )}
            </Paper>
          </Tabs.Panel>

          {params.id !== '0' && facilityId !== undefined && (
            <Tabs.Panel value="eicfTemplates" pt="md">
              <HcfEICFTemplatesPanel facilityId={facilityId} />
            </Tabs.Panel>
          )}

          {params.id !== '0' && facilityId !== undefined && (
            <Tabs.Panel value="invitationEmail" pt="md">
              <HcfInvitationMessagePanel facilityId={facilityId} />
            </Tabs.Panel>
          )}

          {params.id !== '0' && facilityId !== undefined && (
            <Tabs.Panel value="registrationFields" pt="md">
              <HcfRegistrationFieldsPanel facilityId={facilityId} />
            </Tabs.Panel>
          )}

          {params.id !== '0' && (
            <Tabs.Panel value="subscription" pt="md">
              <Paper shadow="sm" radius="md" p="md">
                <Flex justify="space-between" align="center" mb="md">
                  <Title order={3}>
                    {t('clinicSubscriptions.form.subscriptionOverview')}
                  </Title>
                  {subscription &&
                    subscription.status === 'ACTIVE' &&
                    !isEditingSubscription && (
                      <Group gap="sm">
                        <Button
                          variant="outline"
                          leftSection={<IconEdit size={16} />}
                          onClick={() => setIsEditingSubscription(true)}
                        >
                          {t('button.edit')}
                        </Button>
                        <Button
                          color="red"
                          leftSection={<IconTrash size={16} />}
                          onClick={openDeleteSubscriptionModal}
                          loading={deleteClinicSubscriptionMutation.isPending}
                        >
                          {t('clinicSubscriptions.actions.deleteSubscription')}
                        </Button>
                      </Group>
                    )}
                </Flex>

                {subscriptionError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    mb="md"
                    onClose={() => setSubscriptionError(null)}
                  >
                    {subscriptionError}
                  </Alert>
                )}

                {isLoadingSubscription ? (
                  <Loader />
                ) : subscriptionLoadError ? (
                  <Notification color="red">
                    {String(subscriptionLoadError)}
                  </Notification>
                ) : !subscription ||
                  subscription.status === 'NO_SUBSCRIPTION' ? (
                  <>
                    {!showCreateSubscriptionForm ? (
                      <Stack gap="md">
                        <Text c="dimmed">
                          {t('clinicSubscriptions.noSubscriptionsFound')}
                        </Text>
                        <Button
                          onClick={() => setShowCreateSubscriptionForm(true)}
                        >
                          {t('clinicSubscriptions.addNewSubscription')}
                        </Button>
                      </Stack>
                    ) : (
                      <Paper shadow="sm" radius="md" p="md" withBorder>
                        <form
                          onSubmit={subscriptionForm.onSubmit(
                            handleCreateSubscription,
                          )}
                        >
                          <Stack gap="md">
                            <Title order={4}>
                              {t('clinicSubscriptions.form.createTitle')}
                            </Title>

                            <Grid>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.totalSeats',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.enterTotalSeats',
                                  )}
                                  min={1}
                                  max={MAX_CLINIC_SEATS}
                                  {...subscriptionForm.getInputProps(
                                    'totalSeats',
                                  )}
                                  required
                                />
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.durationMonths',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.durationPlaceholder',
                                  )}
                                  min={1}
                                  max={60}
                                  {...subscriptionForm.getInputProps(
                                    'durationMonths',
                                  )}
                                  required
                                />
                              </Grid.Col>
                            </Grid>

                            <Grid>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.pricePerSeat',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.enterPricePerSeat',
                                  )}
                                  min={0}
                                  decimalScale={2}
                                  fixedDecimalScale
                                  {...subscriptionForm.getInputProps(
                                    'pricePerSeat',
                                  )}
                                  required
                                />
                              </Grid.Col>
                            </Grid>

                            <Textarea
                              label={t('clinicSubscriptions.form.notes')}
                              placeholder={t(
                                'clinicSubscriptions.form.notesPlaceholder',
                              )}
                              rows={3}
                              maxLength={2000}
                              {...subscriptionForm.getInputProps('notes')}
                            />

                            <Group justify="flex-end">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowCreateSubscriptionForm(false);
                                  subscriptionForm.reset();
                                }}
                              >
                                {t('clinicSubscriptions.form.cancel')}
                              </Button>
                              <Button
                                type="submit"
                                loading={
                                  createClinicSubscriptionMutation.isPending
                                }
                              >
                                {t(
                                  'clinicSubscriptions.form.createSubscription',
                                )}
                              </Button>
                            </Group>
                          </Stack>
                        </form>
                      </Paper>
                    )}
                  </>
                ) : subscription ? (
                  <>
                    {isEditingSubscription ? (
                      <Paper shadow="sm" radius="md" p="md" withBorder>
                        <form
                          onSubmit={editSubscriptionForm.onSubmit(
                            handleUpdateSubscription,
                          )}
                        >
                          <Stack gap="md">
                            <Title order={4}>
                              {t('clinicSubscriptions.form.editTitle') ||
                                'Edit Subscription'}
                            </Title>

                            <Grid>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.totalSeats',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.enterTotalSeats',
                                  )}
                                  min={subscription.usedSeats}
                                  max={MAX_CLINIC_SEATS}
                                  description={
                                    subscription.usedSeats > 0
                                      ? `Minimum ${subscription.usedSeats} (currently in use)`
                                      : undefined
                                  }
                                  {...editSubscriptionForm.getInputProps(
                                    'totalSeats',
                                  )}
                                  required
                                />
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.durationMonths',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.durationPlaceholder',
                                  )}
                                  min={1}
                                  max={60}
                                  {...editSubscriptionForm.getInputProps(
                                    'durationMonths',
                                  )}
                                  required
                                />
                              </Grid.Col>
                            </Grid>

                            <Grid>
                              <Grid.Col span={6}>
                                <NumberInput
                                  label={t(
                                    'clinicSubscriptions.form.pricePerSeat',
                                  )}
                                  placeholder={t(
                                    'clinicSubscriptions.form.enterPricePerSeat',
                                  )}
                                  min={0}
                                  decimalScale={2}
                                  fixedDecimalScale
                                  {...editSubscriptionForm.getInputProps(
                                    'pricePerSeat',
                                  )}
                                  required
                                />
                              </Grid.Col>
                            </Grid>

                            <Textarea
                              label={t('clinicSubscriptions.form.notes')}
                              placeholder={t(
                                'clinicSubscriptions.form.notesPlaceholder',
                              )}
                              rows={3}
                              maxLength={2000}
                              {...editSubscriptionForm.getInputProps('notes')}
                            />

                            <Group justify="flex-end">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setIsEditingSubscription(false);
                                  editSubscriptionForm.reset();
                                }}
                              >
                                {t('clinicSubscriptions.form.cancel')}
                              </Button>
                              <Button
                                type="submit"
                                loading={
                                  updateClinicSubscriptionMutation.isPending
                                }
                              >
                                {t('button.save')}
                              </Button>
                            </Group>
                          </Stack>
                        </form>
                      </Paper>
                    ) : (
                      <Stack gap="md">
                        <Grid>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t(
                                  'clinicSubscriptions.form.overview.totalSeats',
                                )}
                              </Text>
                              <Text size="xl" fw={700}>
                                {subscription.totalSeats}
                              </Text>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t(
                                  'clinicSubscriptions.form.overview.usedSeats',
                                )}
                              </Text>
                              <Text size="xl" fw={700}>
                                {subscription.usedSeats}
                              </Text>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t(
                                  'clinicSubscriptions.form.overview.availableSeats',
                                )}
                              </Text>
                              <Text size="xl" fw={700}>
                                {subscription.availableSeats}
                              </Text>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t(
                                  'clinicSubscriptions.form.overview.durationMonths',
                                )}
                              </Text>
                              <Text size="xl" fw={700}>
                                {subscription.durationMonths}
                              </Text>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t('clinicSubscriptions.form.overview.status')}
                              </Text>
                              <Badge
                                color={
                                  subscription.status === 'ACTIVE'
                                    ? 'green'
                                    : 'red'
                                }
                                variant="light"
                                size="lg"
                              >
                                {subscription.status}
                              </Badge>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Card>
                              <Text size="sm" c="dimmed">
                                {t(
                                  'clinicSubscriptions.form.overview.pricePerSeat',
                                )}
                              </Text>
                              <Text size="xl" fw={700}>
                                <NumberFormatter
                                  value={subscription.pricePerSeat}
                                  prefix="$"
                                  decimalScale={2}
                                  fixedDecimalScale
                                />
                              </Text>
                            </Card>
                          </Grid.Col>
                        </Grid>

                        {subscription.notes && (
                          <Card withBorder>
                            <Text size="sm" c="dimmed" mb="xs" fw={500}>
                              {t('clinicSubscriptions.form.notes')}
                            </Text>
                            <Text
                              size="sm"
                              style={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                              }}
                            >
                              {subscription.notes}
                            </Text>
                          </Card>
                        )}

                        <Paper shadow="sm" radius="md" p="md" withBorder>
                          <Stack gap="md">
                            <Title order={4}>
                              {t(
                                'clinicSubscriptions.form.overview.activeUsers',
                              )}
                            </Title>

                            {subscription.activeUsers &&
                            subscription.activeUsers.length > 0 ? (
                              <>
                                <TextInput
                                  placeholder={t('global.search')}
                                  rightSection={<IconSearch />}
                                  value={searchUsers}
                                  radius={'md'}
                                  onChange={(el) =>
                                    setSearchUsers(el.currentTarget.value)
                                  }
                                />
                                <ScrollArea>
                                  <Table striped highlightOnHover>
                                    <Table.Thead>
                                      <Table.Tr>
                                        <Th
                                          sorted={sortBy === 'userName'}
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting('userName', sortedData)
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.userName',
                                          )}
                                        </Th>
                                        <Th
                                          sorted={sortBy === 'userEmail'}
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting('userEmail', sortedData)
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.userEmail',
                                          )}
                                        </Th>
                                        <Th
                                          sorted={
                                            sortBy === 'contactableForResearch'
                                          }
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting(
                                              'contactableForResearch',
                                              sortedData,
                                            )
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.contactConsent',
                                          )}
                                        </Th>
                                        <Th
                                          sorted={sortBy === 'pricePaid'}
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting('pricePaid', sortedData)
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.pricePaid',
                                          )}
                                        </Th>
                                        <Th
                                          sorted={
                                            sortBy ===
                                            'durationMonthsAtAssignment'
                                          }
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting(
                                              'durationMonthsAtAssignment',
                                              sortedData,
                                            )
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.duration',
                                          )}
                                        </Th>

                                        <Th
                                          sorted={sortBy === 'accessEndDate'}
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting(
                                              'accessEndDate',
                                              sortedData,
                                            )
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.accessEnd',
                                          )}
                                        </Th>
                                        <Th
                                          sorted={sortBy === 'status'}
                                          reversed={reverseSortDirection}
                                          onSort={() =>
                                            setSorting('status', sortedData)
                                          }
                                        >
                                          {t(
                                            'clinicSubscriptions.form.overview.status',
                                          )}
                                        </Th>
                                        <Table.Th>
                                          {t(
                                            'clinicSubscriptions.form.overview.actions',
                                          )}
                                        </Table.Th>
                                      </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                      {sortedData.map((user: any) => {
                                        const isSearch =
                                          containsStringIgnoreCase(
                                            [
                                              user.userName || '',
                                              user.userEmail || '',
                                              user.userID || '',
                                              user.status || '',
                                            ],
                                            searchUsers,
                                          ) || !searchUsers;

                                        return (
                                          <Table.Tr
                                            key={user.id}
                                            hidden={!isSearch}
                                          >
                                            <Table.Td>
                                              <Highlight
                                                highlight={searchUsers}
                                                highlightStyles={{
                                                  color:
                                                    'var(--mantine-color-gray-9)',
                                                  fontWeight: 700,
                                                  WebkitBackgroundClip: 'text',
                                                }}
                                                styles={{
                                                  root: {
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                  },
                                                }}
                                              >
                                                {user.userName || user.userID}
                                              </Highlight>
                                            </Table.Td>
                                            <Table.Td>
                                              <Highlight
                                                highlight={searchUsers}
                                                highlightStyles={{
                                                  color:
                                                    'var(--mantine-color-gray-9)',
                                                  fontWeight: 700,
                                                  WebkitBackgroundClip: 'text',
                                                }}
                                                styles={{
                                                  root: {
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                  },
                                                }}
                                              >
                                                {user.userEmail || '-'}
                                              </Highlight>
                                            </Table.Td>
                                            <Table.Td>
                                              <Badge
                                                color={
                                                  user.contactableForResearch
                                                    ? 'green'
                                                    : 'gray'
                                                }
                                                variant="light"
                                              >
                                                {user.contactableForResearch
                                                  ? t('common.yes')
                                                  : t('common.no')}
                                              </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                              <NumberFormatter
                                                value={user.pricePaid || 0}
                                                prefix="$"
                                                decimalScale={2}
                                                fixedDecimalScale
                                              />
                                            </Table.Td>
                                            <Table.Td>
                                              {user.durationMonthsAtAssignment
                                                ? t(
                                                    'clinicSubscriptions.form.overview.monthsCount',
                                                    {
                                                      count:
                                                        user.durationMonthsAtAssignment,
                                                    },
                                                  )
                                                : '-'}
                                            </Table.Td>
                                            <Table.Td>
                                              {new Date(
                                                user.accessEndDate,
                                              ).toLocaleDateString()}
                                            </Table.Td>
                                            <Table.Td>
                                              <Badge
                                                color={
                                                  user.status === 'ACTIVE'
                                                    ? 'green'
                                                    : 'red'
                                                }
                                                variant="light"
                                              >
                                                <Highlight
                                                  highlight={searchUsers}
                                                  highlightStyles={{
                                                    color: 'inherit',
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  {user.status}
                                                </Highlight>
                                              </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                              <ActionIcon
                                                color="red"
                                                variant="light"
                                                onClick={() =>
                                                  openReleaseSeatModal(
                                                    user.userID,
                                                  )
                                                }
                                                loading={
                                                  releaseClinicSeatMutation.isPending
                                                }
                                              >
                                                <IconTrash size={16} />
                                              </ActionIcon>
                                            </Table.Td>
                                          </Table.Tr>
                                        );
                                      })}
                                    </Table.Tbody>
                                  </Table>
                                </ScrollArea>
                              </>
                            ) : (
                              <Text c="dimmed" ta="center">
                                {t(
                                  'clinicSubscriptions.form.overview.noActiveUsers',
                                )}
                              </Text>
                            )}
                          </Stack>
                        </Paper>
                      </Stack>
                    )}
                  </>
                ) : null}
              </Paper>
            </Tabs.Panel>
          )}
        </Tabs>
      </>
    );
  }),
);

const WrappedHcfForm: React.FC = () => {
  return (
    <TableContextProvider>
      <HcfForm />
    </TableContextProvider>
  );
};

export default WrappedHcfForm;
